// Linha de split (WEB-123), no molde das listas do painel: a área principal é um `<button>`
// que abre o drawer, e a ação da ponta fica AO LADO dele, nunca dentro (botão dentro de botão
// é HTML inválido).
//
// Recebidos: `source_seller.display_name`, e da transação do pagador só `id` (copiável) e
// `amount` (`19` §5.2). Nada de cliente, itens, metadata ou e-mail do pagador: a API não
// manda, e o teste unit prova que a linha não inventa.
// Enviados: a visão reduzida não tem o recebedor (`source_seller` é o próprio pagador), então
// a linha identifica a cobrança e aponta para ela ("Questões abertas" de WEB-123).
//
// Uma árvore só, que se rearruma por grade: no celular o valor sobe para a ponta da primeira
// linha e o selo desce para a segunda (a anatomia do `ListRow`); a partir de `md` vira uma
// faixa com a cobrança de origem em coluna própria.

import { type ReactNode, useEffect, useState } from 'react';
import type { Split, SplitRole } from '../../api/queries/splits.ts';
import { Check, Copy } from '../../components/icons/index.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { cn } from '../../lib/cn.ts';
import { formatDate } from '../../lib/dates.ts';
import { formatCents } from '../../lib/money.ts';
import { SPLIT_STATUS, splitAmountLabel, splitKindLabel, splitWhen, TONE_BADGE } from './model.ts';

export function SplitStatusChip({ status }: { status: Split['status'] }) {
  const { label, tone } = SPLIT_STATUS[status];
  return (
    <Badge variant={TONE_BADGE[tone]} dot>
      {label}
    </Badge>
  );
}

/** Copia o `transaction.id`: é o que concilia as duas pontas (`19` §5.2). */
export function CopyIdButton({ id, label }: { id: string; label: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = window.setTimeout(() => {
      setCopied(false);
    }, 1600);
    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={copied ? 'Copiado' : label}
      title={copied ? 'Copiado' : label}
      className={copied ? undefined : 'text-muted-foreground hover:text-foreground'}
      onClick={() => {
        void navigator.clipboard?.writeText(id).then(() => {
          setCopied(true);
        });
      }}
    >
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
    </Button>
  );
}

export function SplitRow({
  split,
  side,
  selected = false,
  onSelect,
  transactionLink,
}: {
  split: Split;
  side: SplitRole;
  selected?: boolean;
  onSelect: () => void;
  /** Só na aba Enviados: o link para `/app/transactions/:id` (o recebedor não tem acesso). */
  transactionLink?: ReactNode;
}) {
  const received = side === 'received';
  const title = received
    ? split.source_seller.display_name
    : `Cobrança de ${formatCents(split.transaction.amount)}`;
  const pending = split.amount === null;

  return (
    <div
      data-testid="split-row"
      data-state={selected ? 'selected' : undefined}
      className="flex items-center gap-1 pr-2.5 transition-colors duration-150 hover:bg-secondary/60 data-[state=selected]:bg-secondary sm:pr-4"
    >
      <button
        type="button"
        onClick={onSelect}
        aria-label={`Abrir split ${received ? `de ${title}` : `da ${title.toLowerCase()}`}`}
        className="grid min-w-0 flex-1 cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 py-3.5 pr-1 pl-4 text-left -outline-offset-2 sm:pl-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)_6.5rem_7rem] md:gap-x-5"
      >
        <span className="flex min-w-0 flex-col gap-0.5 leading-tight">
          <span className="truncate text-sm font-medium text-foreground">{title}</span>
          <span className="truncate text-xs text-muted-foreground">
            {splitKindLabel(split)} · {formatDate(splitWhen(split))}
          </span>
        </span>
        <span className="col-start-1 row-start-2 truncate text-xs text-muted-foreground md:col-start-auto md:row-start-auto">
          {received ? `PIX de ${formatCents(split.transaction.amount)} · ` : ''}
          <span className="font-mono">{split.transaction.id}</span>
        </span>
        <span className="col-start-2 row-start-2 justify-self-end md:col-start-auto md:row-start-auto md:justify-self-start">
          <SplitStatusChip status={split.status} />
        </span>
        <span
          className={cn(
            'col-start-2 row-start-1 justify-self-end whitespace-nowrap md:col-start-auto md:row-start-auto',
            pending ? 'text-xs text-muted-foreground' : 'data-value text-sm font-medium',
          )}
        >
          {splitAmountLabel(split)}
        </span>
      </button>
      {received ? (
        <CopyIdButton id={split.transaction.id} label="Copiar id da transação" />
      ) : (
        transactionLink
      )}
    </div>
  );
}
